/**
 * Настройки комнаты — модалка этапа 2.
 *
 * РЕЖИМЫ: просмотр (read-only) → кнопка «Изменить» → редактирование.
 * Видимость каждого контрола решает permissions.ts (useRoomPermissions):
 * нет флага — нет кнопки «Изменить» для этого поля. Серверный 403 здесь
 * невозможен по построению.
 *
 * STATE EVENTS: все изменения — полный content, читаем текущий state,
 * меняем одно поле, отправляем обратно всё (см. roomActions.ts).
 *
 * ДИЗАЙН ЧЕРНОВОЙ: те же Dialog/Field, что и везде. Полировка — этап 6.
 */
import { useRef, useState } from "react";
import { useT, useTF } from "../i18n";
import { EventType, type MatrixClient, type Room } from "matrix-js-sdk";

import { useRoomPermissions } from "../matrix/permissions";
import {
  humanError,
  setHistoryVisibility,
  setJoinRules,
  setRoomAvatar,
  setRoomName,
  setRoomTopic,
} from "../matrix/roomActions";
import { AVATAR_ACCEPT } from "../matrix/avatarImage";
import { useAuthedMedia } from "../matrix/media";
import { Dialog, DialogButton, Field } from "./Dialog";
import { useImagePicker, visuallyHiddenInput } from "./useImagePicker";
import { PickerNoteLine } from "./PickerNoteLine";
import { s } from "./styles";
import { color, font, space } from "./theme";

export function RoomSettings({
  client,
  room,
  onClose,
  extraActions,
}: {
  client: MatrixClient;
  room: Room;
  onClose: () => void;
  /**
   * Дополнительные строки внизу экрана (Антон 2026-09-12 12:55:
   * «остальное уходит в настройки»). Из меню рельсы убраны
   * «Пригласить» и «Участники» — их новая точка входа здесь.
   * RoomSettings сам не знает про диалоги пространства и не должен:
   * содержимое готовит вызывающая сторона.
   */
  extraActions?: React.ReactNode;
}) {
  const t = useT();
  const tfmt = useTF();
  const perms = useRoomPermissions(client, room);

  // ПОДПИСИ ПО ТИПУ (Антон 2026-09-12 12:48: «стоит аватар спейса, а написано
  // аватар комнаты»). Одна и та же модалка открывается и для комнаты
  // (RoomPage), и для ПРОСТРАНСТВА (RoomDialogs -> SpaceSettings), поэтому
  // жёсткое слово «комната» врало ровно в половине случаев.
  // isSpaceRoom() — штатный признак m.space из matrix-js-sdk, а не догадка
  // по имени или по наличию детей.
  // ПАДЕЖИ НЕ ПОДСТАВЛЯЕМ (решение Антона, фаза 2б): вместо «Аватар ${слово}»
  // берём ГОТОВЫЙ цельный ключ по isSpace. Подстановка слова в шаблон рушит
  // es/pt (род и предлог), поэтому на каждый случай два отдельных ключа.
  const isSpace = room.isSpaceRoom();

  // Текущие значения из state комнаты. Читаем напрямую — подписка на
  // RoomStateEvent.Events уже есть в useRoomPermissions, пересчёт при
  // любом изменении state.
  const nameEvent = room.currentState.getStateEvents(EventType.RoomName, "");
  const topicEvent = room.currentState.getStateEvents(EventType.RoomTopic, "");
  const avatarEvent = room.currentState.getStateEvents(EventType.RoomAvatar, "");
  const joinRulesEvent = room.currentState.getStateEvents("m.room.join_rules", "");
  const historyEvent = room.currentState.getStateEvents("m.room.history_visibility", "");

  const currentName = (nameEvent?.getContent()?.name as string) ?? "";
  const currentTopic = (topicEvent?.getContent()?.topic as string) ?? "";
  const currentAvatarMxc = (avatarEvent?.getContent()?.url as string) ?? null;
  const currentJoinRule = (joinRulesEvent?.getContent()?.join_rule as string) ?? "invite";
  const currentHistory = (historyEvent?.getContent()?.history_visibility as string) ?? "shared";

  // Режим редактирования: какое поле сейчас редактируем (или null — просмотр).
  const [editing, setEditing] = useState<
    | { field: "name"; value: string }
    | { field: "topic"; value: string }
    | { field: "joinRules"; value: "public" | "invite" }
    | { field: "history"; value: "shared" | "joined" }
    | null
  >(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
      setEditing(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const avatarUrl = useAuthedMedia(client, currentAvatarMxc);

  const canEditAnything =
    perms.canChangeName ||
    perms.canChangeTopic ||
    perms.canChangeAvatar ||
    perms.canChangeJoinRules ||
    perms.canChangeHistoryVisibility;

  return (
    <Dialog
      title={
        isSpace
          ? tfmt("dialog.spaceSettings", { space: room.name || room.roomId })
          : tfmt("dialog.roomSettings", { room: room.name || room.roomId })
      }
      onClose={onClose}
      error={error}
      footer={
        <DialogButton onClick={onClose} testId="settings-close">{t("common.close")}</DialogButton>
      }
    >
      {/* Аватар */}

      <div style={{ display: "flex", alignItems: "center", gap: space.md }}>
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            overflow: "hidden",
            background: color.sectionBg,
            display: "grid",
            placeItems: "center",
            flex: "0 0 auto",
          }}
        >
          {avatarUrl.url ? (
            <img
              src={avatarUrl.url}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <span style={{ fontSize: 24, color: color.sectionText }}>
              {(room.name || room.roomId).slice(0, 2).toUpperCase()}
            </span>
          )}
        </div>
        <div style={{ flex: "1 1 auto", minWidth: 0 }}>
          <div style={{ fontSize: font.small, color: color.textDim }}>
            {isSpace ? t("dialog.avatarOfSpace") : t("dialog.avatarOfRoom")}
          </div>
          {perms.canChangeAvatar && (
            <AvatarEditor
              client={client}
              room={room}
              onError={setError}
              onDone={() => {}}
            />
          )}
        </div>
      </div>

      {/* Имя */}

      <SettingsRow
        label={t("dialog.newRoomNameLabel")}
        value={currentName || t("dialog.untitled")}
        canEdit={perms.canChangeName}
        editing={editing?.field === "name"}
        onEdit={() => setEditing({ field: "name", value: currentName })}
      >
        <Field
          label={t("dialog.newName")}
          value={editing?.field === "name" ? editing.value : ""}
          onChange={(v) => setEditing({ field: "name", value: v })}
          testId="settings-name-input"
          onEnter={() => {
            if (editing?.field === "name") {
              void run(async () => {
                try {
                  await setRoomName(client, room, editing.value.trim());
                } catch (e) {
                  throw new Error(humanError(e));
                }
                setEditing(null);
              });
            }
          }}
        />
        <div style={{ display: "flex", gap: space.sm, justifyContent: "flex-end" }}>
          <DialogButton onClick={() => setEditing(null)} disabled={busy}>{t("common.cancel")}</DialogButton>
          <DialogButton
            kind="primary"
            disabled={busy}
            testId="settings-name-save"
            onClick={() => {
              if (editing?.field === "name") {
                void run(async () => {
                  try {
                    await setRoomName(client, room, editing.value.trim());
                  } catch (e) {
                    throw new Error(humanError(e));
                  }
                  setEditing(null);
                });
              }
            }}
          >{t("common.save")}</DialogButton>
        </div>
      </SettingsRow>

      {/* Topic */}

      <SettingsRow
        label={t("ui.tema")}
        value={currentTopic || t("dialog.topicUnset")}
        canEdit={perms.canChangeTopic}
        editing={editing?.field === "topic"}
        onEdit={() => setEditing({ field: "topic", value: currentTopic })}
      >
        <Field
          label={t("dialog.newTopic")}
          value={editing?.field === "topic" ? editing.value : ""}
          onChange={(v) => setEditing({ field: "topic", value: v })}
          testId="settings-topic-input"
          onEnter={() => {
            if (editing?.field === "topic") {
              void run(async () => {
                try {
                  await setRoomTopic(client, room, editing.value.trim());
                } catch (e) {
                  throw new Error(humanError(e));
                }
                setEditing(null);
              });
            }
          }}
        />
        <div style={{ display: "flex", gap: space.sm, justifyContent: "flex-end" }}>
          <DialogButton onClick={() => setEditing(null)} disabled={busy}>{t("common.cancel")}</DialogButton>
          <DialogButton
            kind="primary"
            disabled={busy}
            testId="settings-topic-save"
            onClick={() => {
              if (editing?.field === "topic") {
                void run(async () => {
                  try {
                    await setRoomTopic(client, room, editing.value.trim());
                  } catch (e) {
                    throw new Error(humanError(e));
                  }
                  setEditing(null);
                });
              }
            }}
          >{t("common.save")}</DialogButton>
        </div>
      </SettingsRow>

      {/* Join rules */}

      <SettingsRow
        label={isSpace ? t("dialog.joinSpace") : t("dialog.joinRoom")}
        value={currentJoinRule === "public" ? t("dialog.publicJoin") : t("dialog.joinByInvite")}
        canEdit={perms.canChangeJoinRules}
        editing={editing?.field === "joinRules"}
        onEdit={() =>
          setEditing({
            field: "joinRules",
            value: currentJoinRule === "public" ? "public" : "invite",
          })
        }
      >
        <label style={{ display: "block", fontSize: font.small, color: color.textDim }}>{t("dialog.joinMode")}<select
            style={s.dialogInput}
            value={editing?.field === "joinRules" ? editing.value : "invite"}
            data-testid="settings-join-rules-select"
            onChange={(e) =>
              setEditing({
                field: "joinRules",
                value: e.target.value as "public" | "invite",
              })
            }
          >
            <option value="invite">{t("dialog.joinByInvite")}</option>
            <option value="public">{t("room.publicWord")}</option>
          </select>
        </label>
        <div style={{ fontSize: font.micro, color: color.textDim }}>
          {isSpace
            ? t("ui.publichnoe_prostranstvo_vidno_v_kataloge")
            : t("ui.publichnaya_komnata_vidna_v_kataloge_serve")}
        </div>
        <div style={{ display: "flex", gap: space.sm, justifyContent: "flex-end" }}>
          <DialogButton onClick={() => setEditing(null)} disabled={busy}>{t("common.cancel")}</DialogButton>
          <DialogButton
            kind="primary"
            disabled={busy}
            testId="settings-join-rules-save"
            onClick={() => {
              if (editing?.field === "joinRules") {
                void run(async () => {
                  try {
                    await setJoinRules(client, room, editing.value);
                  } catch (e) {
                    throw new Error(humanError(e));
                  }
                  setEditing(null);
                });
              }
            }}
          >{t("common.save")}</DialogButton>
        </div>
      </SettingsRow>

      {/* History visibility */}

      <SettingsRow
        label={t("dialog.historyVisibility")}
        value={
          currentHistory === "shared"
            ? t("dialog.historyShared")
            : t("dialog.historyJoined")
        }
        canEdit={perms.canChangeHistoryVisibility}
        editing={editing?.field === "history"}
        onEdit={() =>
          setEditing({
            field: "history",
            value: currentHistory === "shared" ? "shared" : "joined",
          })
        }
      >
        <label style={{ display: "block", fontSize: font.small, color: color.textDim }}>{t("dialog.historyWho")}<select
            style={s.dialogInput}
            value={editing?.field === "history" ? editing.value : "shared"}
            data-testid="settings-history-select"
            onChange={(e) =>
              setEditing({
                field: "history",
                value: e.target.value as "shared" | "joined",
              })
            }
          >
            <option value="shared">{t("room.historyShared")}</option>
            <option value="joined">{t("room.historyJoined")}</option>
          </select>
        </label>
        <div style={{ fontSize: font.micro, color: color.textDim }}>{t("dialog.historyHint")}</div>
        <div style={{ display: "flex", gap: space.sm, justifyContent: "flex-end" }}>
          <DialogButton onClick={() => setEditing(null)} disabled={busy}>{t("common.cancel")}</DialogButton>
          <DialogButton
            kind="primary"
            disabled={busy}
            testId="settings-history-save"
            onClick={() => {
              if (editing?.field === "history") {
                void run(async () => {
                  try {
                    await setHistoryVisibility(client, room, editing.value);
                  } catch (e) {
                    throw new Error(humanError(e));
                  }
                  setEditing(null);
                });
              }
            }}
          >{t("common.save")}</DialogButton>
        </div>
      </SettingsRow>

      {!canEditAnything && (
        <div style={{ fontSize: font.small, color: color.textDim }}>
          {isSpace
            ? t("dialog.noSettingsPermissionSpace")
            : t("dialog.noSettingsPermissionRoom")}
        </div>
      )}

      {extraActions}
    </Dialog>
  );
}

/** Строка настройки: лейбл + значение + кнопка «Изменить» (или форма). */
function SettingsRow({
  label,
  value,
  canEdit,
  editing,
  onEdit,
  children,
}: {
  label: string;
  value: string;
  canEdit: boolean;
  editing: boolean;
  onEdit: () => void;
  children: React.ReactNode;
}) {
  const t = useT();
  return (
    <div
      style={{
        borderTop: `1px solid ${color.border}`,
        paddingTop: space.md,
        display: "flex",
        flexDirection: "column",
        gap: space.sm,
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: space.md }}>
        <div style={{ flex: "1 1 auto", minWidth: 0 }}>
          <div style={{ fontSize: font.micro, color: color.textDim, textTransform: "uppercase" }}>
            {label}
          </div>
          <div
            style={{
              fontSize: font.small,
              color: color.text,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {value}
          </div>
        </div>
        {canEdit && !editing && (
          <button
            type="button"
            style={s.actionButton}
            onClick={onEdit}
            data-testid={`settings-edit-${label}`}
          >{t("timeline.edit")}</button>
        )}
      </div>
      {editing && (
        <div style={{ display: "flex", flexDirection: "column", gap: space.sm }}>
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Редактор аватара: выбор файла + удаление.
 *
 * Проп был `busy` из родителя — УБРАН (Антон 24.09). Он гасил кнопку выбора
 * файла, пока шла ЛЮБАЯ другая операция диалога (переименование, тема,
 * правила входа) и навсегда — если та операция зависла. Собственное
 * состояние загрузки достаточно и живёт здесь.
 */
function AvatarEditor({
  client,
  room,
  onError,
  onDone,
}: {
  client: MatrixClient;
  room: Room;
  onError: (e: string | null) => void;
  onDone: () => void;
}) {
  const t = useT();
  const [uploading, setUploading] = useState(false);
  // См. ProfileView: состояние в замыкании обработчика устаревает, ref — нет.
  const uploadingRef = useRef(false);

  const pick = async (file: File) => {
    if (uploadingRef.current) throw new Error(t("picker.busy"));
    uploadingRef.current = true;
    onError(null);
    setUploading(true);
    try {
      await setRoomAvatar(client, room, file);
      onDone();
    } catch (e) {
      // Ошибку покажет строка под кнопкой (её точно видно рядом с местом
      // действия), плюс общая плашка диалога через onError выше по дереву.
      throw new Error(humanError(e));
    } finally {
      // Сброс ВСЕГДА, включая ветки ошибок.
      uploadingRef.current = false;
      setUploading(false);
    }
  };

  // Единый механизм выбора файла (см. useImagePicker): input в раскладке,
  // id уникален, value сбрасывается, есть видимая человеку диагностика.
  const picker = useImagePicker({ onFile: pick, onError: (m) => onError(m) });

  const remove = async () => {
    if (uploadingRef.current) return;
    uploadingRef.current = true;
    onError(null);
    setUploading(true);
    try {
      await setRoomAvatar(client, room, null);
      onDone();
    } catch (e) {
      onError(humanError(e));
    } finally {
      uploadingRef.current = false;
      setUploading(false);
    }
  };

  return (
    <div style={{ marginTop: space.sm }}>
      <div style={{ display: "flex", gap: space.sm }}>
        <input
          ref={picker.ref}
          type="file"
          accept={AVATAR_ACCEPT}
          style={visuallyHiddenInput}
          id={picker.id}
          tabIndex={-1}
          aria-hidden="true"
          data-testid="settings-avatar-input"
          onChange={picker.onChange}
        />
        {/* Была <label htmlFor> на жёсткий id="room-avatar-input": при открытых
            поверх друг друга настройках комнаты и пространства id задваивался и
            label цеплялся к чужому input. Теперь кнопка + ref.click().

            disabled УБРАН совсем (Антон 24.09, второй заход). Здесь было хуже
            всего: кнопка гасла по `busy` РОДИТЕЛЯ, а тот `busy` общий на весь
            диалог — любая другая незавершённая операция (имя, тема, правила
            входа) молча выключала выбор аватара. Выбор файла не блокируется
            никогда; повторную отправку сдерживает uploadingRef внутри pick. */}
        <button
          type="button"
          onClick={picker.open}
          data-testid="settings-avatar-pick"
          style={{ ...s.actionButton, cursor: "pointer" }}
        >
          {uploading ? t("room.loadingShort") : t("ui.zagruzit")}
        </button>
        {/* Удаление — действительно запись на сервер, его блокировать на время
            своей же загрузки правильно: две встречные записи дадут гонку. */}
        <button
          type="button"
          style={{ ...s.actionButton, color: color.danger }}
          disabled={uploading}
          onClick={() => void remove()}
          data-testid="settings-avatar-remove"
        >{t("common.remove")}</button>
      </div>
      {/* Видимая диагностика без консоли — см. PickerNoteLine. */}
      <PickerNoteLine note={picker.note} testId="settings-avatar-note" />
    </div>
  );
}
